import { io, Socket } from 'socket.io-client';

/**
 * Socket.IO service singleton.
 * Manages the WebSocket connection to the Flask-SocketIO backend.
 * 
 * Event names MUST match the backend exactly:
 * - GET events: /GET/*, [WS] *
 * - PUSH events: /PUSH/*
 * - DELETE events: /DELETE/*
 * - POST events: /POST/*
 */

// ==================== WebSocket Event Constants ====================
// These must match backend event names exactly

// Connection events
export const WS_CONNECT = '[WS] Connected';
export const WS_DISCONNECT = '[WS] Disconnected';
export const WS_RECONNECT = '[WS] Re-connected';

// MenuItem
export const PUSH_MENU_ITEM = '/PUSH/menu_item';
export const GET_MENU_ITEM = '/GET/menu_item';

// MenuGroup
export const PUSH_MENU_GROUP = '/PUSH/menu_group';
export const GET_MENU_GROUP = '/GET/menu_group';

// MenuItemGroupMember
export const PUSH_MENU_ITEM_GROUP_MEMBER = '/PUSH/menu_item_group_member';
export const DELETE_MENU_ITEM_GROUP_MEMBER = '/DELETE/menu_item_group_member';
export const GET_MENU_ITEM_GROUP_MEMBER = '/GET/menu_item_group_member';

// MenuGroupGroupComplement
export const PUSH_MENU_GROUP_GROUP_COMPLEMENT = '/PUSH/menu_group_group_complement';
export const DELETE_MENU_GROUP_GROUP_COMPLEMENT = '/DELETE/menu_group_group_complement';
export const GET_MENU_GROUP_GROUP_COMPLEMENT = '/GET/menu_group_group_complement';

// MenuItemGroupComplement
export const PUSH_MENU_ITEM_GROUP_COMPLEMENT = '/PUSH/menu_item_group_complement';
export const DELETE_MENU_ITEM_GROUP_COMPLEMENT = '/DELETE/menu_item_group_complement';
export const GET_MENU_ITEM_GROUP_COMPLEMENT = '/GET/menu_item_group_complement';

// Delivery
export const PUSH_DELIVERY = '/PUSH/delivery';
export const GET_DELIVERY_DATE = '/GET/delivery/date';
export const GET_DELIVERY_TODAY = '/GET/delivery/today';
export const POST_DELIVERY = '/POST/delivery';

// Call
export const PUSH_CALL = '/PUSH/call';
export const GET_CALL_DATE = '/GET/call/date';
export const GET_CALL_TODAY = '/GET/call/today';

// Location
export const PUSH_LOCATION = '/PUSH/location';
export const DELETE_LOCATION = '/DELETE/location';
export const GET_LOCATION = '/GET/location';

// Checkin
export const PUSH_CHECKIN = '/PUSH/checkin';
export const GET_CHECKIN = '/GET/checkin/latest';

// Delivery Man
export const PUSH_DELIVERYMAN = '/PUSH/deliveryman';
export const DELETE_DELIVERYMAN = '/DELETE/deliveryman';
export const GET_DELIVERYMAN = '/GET/deliveryman';

// Agent
export const GET_AGENT_RECORDS = '[WS] Agents GET';
export const GET_MY_AGENT_KEY = '[WS] Agent GET my key';
export const GET_AGENT_LOCATIONS = '/GET/location/agent';

// User UI settings
export const GET_USER_UI_SETTINGS = '[WS] get user UI settings';

// Customer data
export const FETCH_CUSTOMER_DATA = '[WS] Get customer data';

// Maps and distances
export const GET_ADDRESS_DISTANCE = '/GET/address_distance';

// Printer state (zebraman → backend Redis bridge)
export const PUSH_PRINTER_STATE = '/PUSH/printer_state';

// Online orders paused state
export const PUSH_ONLINE_ORDERS_PAUSED = '/PUSH/online_orders_paused';
export const PUSH_MIN_DELIVERY_TIME = '/PUSH/min_delivery_time';
export const PUSH_RESERVATION = '/PUSH/reservation';

// ==================== Socket Instance ====================

let socket: Socket | null = null;

export function getSocket(): Socket {
  if (!socket) {
    const token = localStorage.getItem('token');
    socket = io('/', {
      query: { token: token || '' },
      transports: ['websocket'],
      autoConnect: false,
    });
  }
  return socket;
}

export function updateSocketToken(token: string): void {
  const s = getSocket();
  (s.io.opts.query as Record<string, string>).token = token;
}

export function connectSocket(): void {
  getSocket().open();
}

export function disconnectSocket(): void {
  getSocket().close();
}

/**
 * Emit a WebSocket event and return a promise with the callback response.
 */
export function emitWithAck<T = unknown>(event: string, ...args: unknown[]): Promise<T> {
  return new Promise((resolve) => {
    getSocket().emit(event, ...args, (data: T) => {
      resolve(data);
    });
  });
}

export default getSocket;
